import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { ReactNode, SyntheticEvent } from 'react';
import zeplinColor from 'theme/Color';

interface StyledTabsProps {
  children?: ReactNode;
  value: number;
  onChange: (event: SyntheticEvent, newValue: number) => void;
}

interface TabPanelProps {
  children?: ReactNode;
  dir?: string;
  index: number;
  value: number;
}

interface customTabProps {
  id: number;
  label: string;
  Component: ReactNode;
  isHidden: boolean;
}

interface IProps {
  tabs: customTabProps[];
  handleChange: (event: SyntheticEvent, newValue: number) => void;
  value: number;
}

const StyledTabs = styled((props: StyledTabsProps) => (
  <Tabs {...props} slotProps={{ indicator: <span className='MuiTabs-indicatorSpan' /> }} />
))({
  minHeight: 0,
  'border-bottom': `1px solid ${zeplinColor.Neutral5}`,

  '& .MuiTabs-flexContainer': {
    display: 'flex',
    gap: '24px',
    scrollbarWidth: 'none',
    overflow: 'scroll',
  },
  '& .MuiTabs-indicator': {
    backgroundColor: 'transparent',
  },
});

interface StyledTabProps {
  label: string;
}

const StyledTab = styled((props: StyledTabProps) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    textTransform: 'capitalize',
    color: zeplinColor.Neutral8,
    padding: '0 3px 12px 3px',
    margin: 0,
    minHeight: 0,
    minWidth: 0,
    width: 'fit-content',

    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '100%',
      height: '2.5px',
      borderRadius: '10px',
      backgroundColor: 'transparent',
      transition: 'background-color 0.2s ease',
    },

    '&:not(.Mui-selected):hover': {
      color: zeplinColor.BasicBlack,
      '&::after': {
        backgroundColor: zeplinColor.BasicBlack,
      },
    },
    '&.Mui-selected': {
      color: zeplinColor.BasicBlack,
      '&::after': {
        backgroundColor: zeplinColor.BasicBlack,
      },
    },
  }),
);

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
      style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'auto' }}
    >
      {value === index && (
        <Box
          sx={{
            p: '0px',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'auto',
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `full-width-tab-${index}`,
    'aria-controls': `full-width-tabpanel-${index}`,
  };
}

function CustomNavigationTab({ tabs, handleChange, value }: IProps) {
  return (
    <Box
      sx={{
        width: '100%',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: '12px', xl: '16px' },
        position: 'relative',
        minHeight: 0,
      }}
    >
      <StyledTabs value={value} onChange={handleChange} aria-label='styled tabs example'>
        {tabs.map((option) => (
          <StyledTab label={option.label} key={option.id} {...a11yProps(option.id)} />
        ))}
      </StyledTabs>
      {tabs.map(
        ({ Component }, i: number) =>
          !tabs[i].isHidden && (
            <TabPanel value={value} index={i} key={i}>
              {Component}
            </TabPanel>
          ),
      )}
    </Box>
  );
}

export default CustomNavigationTab;
